/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  ViewChild,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DialogService } from '@ng-falcon/dialog/dialog.service';
import { LikeService } from '../../services/like.service';
import { openLikeList } from '../../common/community/feed/like-list/community-like-list-dialog.component';
import { itemLikeCount } from '../../common/community/feed/community-item';
import type { CommunityProfileCard } from './community-profile-activity';

// The profile's Forum posts, Comments, Mentions and Likes cards (PROFILE_FEEDS_UNIFIED.html): the community
// feed card's anatomy (context row, author header, title and clamped text, footer link and count).
// Only Forum posts have a like action; the other items don't carry the data for one.
@Component({
  selector: 'ut-community-profile-activity-card',
  templateUrl: './community-profile-activity-card.component.html',
  styleUrls: ['./community-profile-activity-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CommunityProfileActivityCardComponent implements AfterViewInit {
  @Input({ required: true }) card: CommunityProfileCard;
  // The signed-in user, for the likers list.
  @Input() user: any;

  @ViewChild('content') content: ElementRef<HTMLElement> | undefined;

  isClamped = signal(false);
  expanded = signal(false);

  constructor(
    private dialogService: DialogService,
    private cdr: ChangeDetectorRef,
    private likeService: LikeService,
  ) {
    // The Like button updates the topic in place and announces it on LikeService.changed.
    this.likeService.changed.pipe(takeUntilDestroyed()).subscribe((likeable) => {
      if (likeable === this.card.likeable) this.cdr.markForCheck();
    });
  }

  get likeCount(): number {
    return itemLikeCount(this.card.likeable);
  }

  showLikes(): void {
    openLikeList(this.dialogService, this.card.likeable, this.user);
  }

  // The "See more" button is removed once clicked, so focus moves to the text it revealed.
  expand(): void {
    this.expanded.set(true);
    this.content?.nativeElement.focus();
  }

  // Measure after layout so "See more" only shows for clamped text.
  ngAfterViewInit(): void {
    setTimeout(() => {
      const el = this.content?.nativeElement;
      this.isClamped.set(!!el && el.scrollHeight > el.clientHeight);
    });
  }
}
